import {
  Badge,
  Button,
  Checkbox,
  cn,
  Col,
  Label,
  Row,
  Select,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideSlidersHorizontal } from 'lucide-react';

import {
  $discardLocationGeneralPreferencesChanges,
  $displayedLocationGeneralPreferences,
  $hasUnsavedChanges,
  $saveLocationGeneralPreferences,
  $setDefaultUnitSystem,
  $toggleUnitSystem,
  $updateLocationGeneralPreferencesForm,
} from '../../../_state/locationGeneralPreferences.state';
import { FormSection } from '../../_shared/FormSection';

const DATE_FORMAT_OPTIONS = [
  { key: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { key: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { key: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

const WEEK_START_OPTIONS = [
  { key: 'Sunday', label: 'Sunday' },
  { key: 'Monday', label: 'Monday' },
];

const LANGUAGE_OPTIONS = [
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Spanish' },
  { key: 'pt', label: 'Portuguese' },
];

const TIMEZONE_OPTIONS = [
  { key: 'America/New_York', label: 'America/New York' },
  { key: 'America/Chicago', label: 'America/Chicago' },
  { key: 'America/Denver', label: 'America/Denver' },
  { key: 'America/Los_Angeles', label: 'America/Los Angeles' },
  { key: 'America/Sao_Paulo', label: 'America/Sao Paulo' },
  { key: 'Europe/London', label: 'Europe/London' },
  { key: 'UTC', label: 'UTC' },
];

const CURRENCY_OPTIONS = [
  { key: 'USD', label: 'USD (US Dollar)' },
  { key: 'EUR', label: 'EUR (Euro)' },
  { key: 'GBP', label: 'GBP (British Pound)' },
  { key: 'BRL', label: 'BRL (Brazilian Real)' },
];

type UnitSystem = 'Metric' | 'Imperial';

type UnitSystemItem = {
  id: UnitSystem;
  label: string;
  isEnabled: boolean;
  isDefault: boolean;
};

type UnitSystemCheckboxProps = {
  item: UnitSystemItem;
  onToggle: (id: UnitSystem, enabled: boolean) => void;
  onSetDefault: (id: UnitSystem) => void;
};

const UnitSystemCheckbox = ({
  item,
  onToggle,
  onSetDefault,
}: UnitSystemCheckboxProps) => (
  <Row yCenter className="@UnitSystemCheckbox justify-between py-1">
    <Checkbox
      isSelected={item.isEnabled}
      isDisabled={item.isDefault}
      onChange={(v) => onToggle(item.id, v)}
    >
      {item.label}
    </Checkbox>
    {item.isDefault ? (
      <Badge color="primary">Default</Badge>
    ) : (
      <button
        type="button"
        className={cn(
          'cursor-pointer border-none bg-transparent px-1 py-px text-xs whitespace-nowrap opacity-50 transition-all outline-none',
          'rounded hover:bg-current/10 hover:opacity-100',
        )}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => onSetDefault(item.id)}
        tabIndex={-1}
      >
        Make default
      </button>
    )}
  </Row>
);

export const GeneralPreferences = () => {
  const form = useAtomValue($displayedLocationGeneralPreferences);
  const updateForm = useSetAtom($updateLocationGeneralPreferencesForm);
  const toggleUnitSystem = useSetAtom($toggleUnitSystem);
  const setDefaultUnitSystem = useSetAtom($setDefaultUnitSystem);
  const discardChanges = useSetAtom($discardLocationGeneralPreferencesChanges);
  const savePreferences = useSetAtom($saveLocationGeneralPreferences);
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);

  const unitSystems: UnitSystemItem[] = [
    {
      id: 'Metric',
      label: 'Metric',
      isEnabled: form.allowedUnitSystems.includes('Metric'),
      isDefault: form.defaultUnitSystem === 'Metric',
    },
    {
      id: 'Imperial',
      label: 'Imperial',
      isEnabled: form.allowedUnitSystems.includes('Imperial'),
      isDefault: form.defaultUnitSystem === 'Imperial',
    },
  ];

  const handleUnitToggle = (id: UnitSystem, enabled: boolean) => {
    toggleUnitSystem({ unitSystem: id, enabled });
  };

  const handleSetDefaultUnit = (id: UnitSystem) => {
    setDefaultUnitSystem(id);
  };

  const handleDiscard = () => {
    discardChanges();
  };

  const handleSave = () => {
    savePreferences();
  };

  return (
    <Col className="@GeneralPreferences h-full">
      <FormSection
        icon={LucideSlidersHorizontal}
        title="General Preferences"
        className={'max-h-full overflow-y-auto p-5'}
      >
        <div className={'grid grid-cols-2 gap-4'}>
          <Select
            label="Preferred Date Format"
            isRequired
            value={form.dateFormat}
            options={DATE_FORMAT_OPTIONS}
            onChange={(v) => updateForm({ dateFormat: v as string })}
          />
          <Select
            label="Start Week on"
            isRequired
            value={form.weekStartDay}
            options={WEEK_START_OPTIONS}
            onChange={(v) => updateForm({ weekStartDay: v as string })}
          />

          <Select
            label="Language"
            isRequired
            value={form.language}
            options={LANGUAGE_OPTIONS}
            onChange={(v) => updateForm({ language: v as string })}
          />
          <Select
            label="Timezone"
            isRequired
            value={form.timezone}
            options={TIMEZONE_OPTIONS}
            onChange={(v) => updateForm({ timezone: v as string })}
          />

          <div className={'col-span-2 grid grid-cols-2 gap-4'}>
            <Select
              label="Default Currency"
              isRequired
              value={form.currency}
              options={CURRENCY_OPTIONS}
              onChange={(v) => updateForm({ currency: v as string })}
            />
          </div>

          <Col gap1>
            <Label aria-required={true}>Allowed Unit Systems</Label>
            <Col>
              {unitSystems.map((item) => (
                <UnitSystemCheckbox
                  key={item.id}
                  item={item}
                  onToggle={handleUnitToggle}
                  onSetDefault={handleSetDefaultUnit}
                />
              ))}
            </Col>
          </Col>
        </div>
      </FormSection>
      <Row xEnd gap3 className={'border-t border-neutral/15 p-5'}>
        <Button
          variant="outline"
          color="neutral"
          isDisabled={!hasUnsavedChanges}
          onClick={handleDiscard}
        >
          Discard
        </Button>
        <Button isDisabled={!hasUnsavedChanges} onClick={handleSave}>
          Save Changes
        </Button>
      </Row>
    </Col>
  );
};
